import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useApp } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card } from '../components/ui/card';
import { Wind, ArrowRight, ShieldCheck, Activity, Building2 } from 'lucide-react';
import { toast } from 'sonner';

export default function Login() {
  const navigate = useNavigate();
  const { login } = useApp();
  const [email, setEmail] = useState('admin@jbnotify.com');
  const [password, setPassword] = useState('demo123');
  const [loading, setLoading] = useState(false);

  const submit = (e) => {
    e.preventDefault();
    setLoading(true);
    setTimeout(() => {
      const res = login(email, password);
      setLoading(false);
      if (res.ok) {
        toast.success('Welcome back, Jordan');
        navigate('/');
      } else {
        toast.error(res.error);
      }
    }, 450);
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-[1.1fr_1fr] bg-white" data-testid="login-page">
      {/* Left: brand / visual */}
      <div
        className="relative hidden lg:flex flex-col justify-between p-12 overflow-hidden"
        style={{
          backgroundImage:
            'linear-gradient(135deg, rgba(7, 24, 58, 0.88) 0%, rgba(0, 82, 204, 0.72) 100%), url("https://images.unsplash.com/photo-1774876549476-254b00a5d648?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2OTV8MHwxfHNlYXJjaHwxfHxtb2Rlcm4lMjBzbWFydCUyMGhvbWUlMjB0aGVybW9zdGF0JTIwaW50ZXJpb3J8ZW58MHx8fHwxNzc3OTE5MDY1fDA&ixlib=rb-4.1.0&q=85")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
        }}
      >
        <div className="flex items-center gap-2.5 text-white">
          <div className="h-9 w-9 rounded-md bg-white/15 border border-white/30 backdrop-blur flex items-center justify-center">
            <Wind className="h-4 w-4" strokeWidth={2.5} />
          </div>
          <div>
            <div className="text-base font-semibold tracking-tight">JB Notify</div>
            <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-white/70">
              HVAC Command Center
            </div>
          </div>
        </div>

        <div className="relative z-10 max-w-lg">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 border border-white/20 backdrop-blur text-white text-xs font-medium mb-6">
            <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse" />
            Live monitoring 2,487 devices
          </div>
          <h1 className="text-white text-4xl lg:text-5xl font-semibold tracking-tight leading-[1.05]">
            Diagnose issues before your customers feel them.
          </h1>
          <p className="mt-5 text-white/75 text-base leading-relaxed max-w-md">
            Real-time Honeywell thermostat monitoring, AI-driven diagnostics, and dispatcher-ready service recommendations — in one command center.
          </p>

          <div className="mt-8 grid grid-cols-3 gap-3 max-w-md">
            <Stat icon={Activity} label="Devices" value="2,487" />
            <Stat icon={ShieldCheck} label="Uptime" value="99.98%" />
            <Stat icon={Building2} label="Sites" value="1,842" />
          </div>
        </div>

        <div className="text-white/60 text-xs font-mono relative z-10">
          © 2026 JB Heating & Air · Enterprise HVAC Intelligence
        </div>
      </div>

      {/* Right: form */}
      <div className="flex items-center justify-center p-6 sm:p-10 bg-[#F7F9FC]">
        <Card className="w-full max-w-md p-8 shadow-sm border-slate-200">
          <div className="lg:hidden flex items-center gap-2.5 mb-6">
            <div className="h-9 w-9 rounded-md bg-[#0052CC] flex items-center justify-center text-white">
              <Wind className="h-4 w-4" strokeWidth={2.5} />
            </div>
            <div>
              <div className="text-base font-semibold tracking-tight">JB Notify</div>
              <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">HVAC Command Center</div>
            </div>
          </div>

          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 font-semibold">Sign in</div>
          <h2 className="mt-2 text-2xl font-semibold text-slate-900 tracking-tight">
            Welcome back to your dashboard
          </h2>
          <p className="mt-1.5 text-sm text-slate-500">
            Monitor, diagnose, and dispatch — all in one place.
          </p>

          <form onSubmit={submit} className="mt-7 space-y-4" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-medium text-slate-700">
                Work Email
              </Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="h-11"
                data-testid="login-email"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-medium text-slate-700">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="h-11"
                data-testid="login-password"
              />
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#0052CC] hover:bg-[#0043A6] text-white font-medium"
              data-testid="login-submit"
            >
              {loading ? 'Signing in...' : (
                <>
                  Sign in to Dashboard <ArrowRight className="h-4 w-4 ml-1" />
                </>
              )}
            </Button>
          </form>

          <div className="mt-6 p-3 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-[10px] uppercase tracking-[0.2em] text-slate-500 font-semibold">Demo credentials</div>
            <div className="mt-1.5 text-xs font-mono text-slate-700">
              admin@jbnotify.com · demo123
            </div>
          </div>
        </Card>
      </div>
    </div>
  );
}

function Stat({ icon: Icon, label, value }) {
  return (
    <div className="rounded-lg bg-white/10 border border-white/20 backdrop-blur p-3">
      <Icon className="h-4 w-4 text-white/80" />
      <div className="mt-2 font-mono text-lg font-semibold text-white leading-none">{value}</div>
      <div className="text-[10px] uppercase tracking-[0.18em] text-white/60 mt-1">{label}</div>
    </div>
  );
}
